<c-container class="mt-4 report-shell">
  <div class="rb-layout">

    <!-- LEFT: config rail -->
    <aside class="config-rail flex-col" data-testid="report-shell-filter-bar">
      <h2 class="rail-title">Report Builder</h2>

      <!-- Game -->
      @if (isCrossGame && adminGames.length > 1) {
        <mat-form-field appearance="outline" class="rail-field">
          <mat-label>Games</mat-label>
          <mat-select multiple [ngModel]="selectedGameIds" (ngModelChange)="onGamesChosen($event)"
                      data-testid="report-shell-game-select">
            @for (g of adminGames; track g.Id) {
              <mat-option [value]="g.Id" [attr.data-testid]="'report-shell-game-option-' + g.Id">{{ g.ShortName || g.Name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        <button type="button" class="link-btn" (click)="toggleAllGames()" data-testid="report-shell-select-all-games">
          {{ allGamesSelected ? 'Clear all' : 'Select all ' + adminGames.length + ' games' }}
        </button>
        @if (isMultiGame) {
          <div class="scope-chip flex align-center" data-testid="report-shell-multi-scope">{{ selectedGameIds.length }} games combined</div>
        }
      } @else {
        <div class="field flex-col">
          <span class="flabel">Game</span>
          <div class="scope-chip flex align-center" data-testid="report-shell-scope">{{ selectedGame?.Name ?? '—' }}</div>
        </div>
      }

      @if (hasScope) {

        <!-- Report (preset) -->
        <mat-form-field appearance="outline" class="rail-field">
          <mat-label>Report</mat-label>
          <mat-select [ngModel]="selectedPresetKey" (ngModelChange)="applyPreset($event)" data-testid="report-shell-preset">
            @for (p of presets; track p.key) {
              <mat-option [value]="p.key" [attr.data-testid]="'report-shell-preset-option-' + p.key">{{ p.label }}</mat-option>
            }
            <mat-option value="custom" data-testid="report-shell-preset-option-custom">Custom…</mat-option>
          </mat-select>
        </mat-form-field>

        <!-- Timeframe -->
        <div class="field flex-col">
          <span class="flabel">Timeframe</span>
          <div class="seg">
            <button type="button" [class.on]="timeframe === 'DateRange'" (click)="setTimeframe('DateRange')"
                    data-testid="report-shell-timeframe-daterange">Date range</button>
            <!-- A draw belongs to one game, so there is nothing for this to mean across several. -->
            @if (!isMultiGame) {
              <button type="button" [class.on]="timeframe === 'SingleDraw'" (click)="setTimeframe('SingleDraw')"
                      data-testid="report-shell-timeframe-singledraw">Single draw</button>
            }
          </div>
        </div>

        @if (timeframe === 'DateRange') {
          <mat-form-field appearance="outline" class="rail-field">
            <mat-label>From – To</mat-label>
            <mat-date-range-input [rangePicker]="reportRangePicker">
              <input matStartDate placeholder="From" [value]="startDateValue"
                     (dateInput)="onStartDateChosen($event.value)" (dateChange)="onStartDateChosen($event.value)"
                     data-testid="report-shell-start-date">
              <input matEndDate placeholder="To" [value]="endDateValue"
                     (dateInput)="onEndDateChosen($event.value)" (dateChange)="onEndDateChosen($event.value)"
                     data-testid="report-shell-end-date">
            </mat-date-range-input>
            <mat-datepicker-toggle matIconSuffix [for]="reportRangePicker" data-testid="report-shell-date-picker-toggle"></mat-datepicker-toggle>
            <mat-date-range-picker #reportRangePicker></mat-date-range-picker>
          </mat-form-field>
          @if (!isMultiGame && instancesLoaded && startDate && endDate && startDate <= endDate) {
            <div class="range-hint flex align-center" data-testid="report-shell-draws-in-range">
              <mat-icon>event</mat-icon>
              <span><strong>{{ drawsInRange | number }}</strong> {{ drawsInRange === 1 ? 'draw' : 'draws' }} in this range</span>
            </div>
          }
        } @else {
          <mat-form-field appearance="outline" class="rail-field">
            <mat-label>Draw</mat-label>
            <mat-select [ngModel]="selectedInstanceId" (ngModelChange)="onInstanceChosen($event)"
                        data-testid="report-shell-draw-select">
              @for (i of instances; track i.Id) {
                <mat-option [value]="i.Id" data-testid="report-shell-draw-option">{{ (i.EndedOn || i.StartedOn) | date:'mediumDate' }} · {{ i.Id.slice(0, 8) }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        }

        <!-- Fields -->
        <div class="field-picker flex-col">
          <span class="flabel">Fields</span>
          @for (group of fieldGroups; track group.title) {
            @if (groupIsAvailable(group)) {
            <div class="fgroup">
              <h4>{{ group.title }}</h4>
              <div class="toggles flex-col">
                @for (f of group.fields; track f.key) {
                  @if (fieldIsAvailable(f.key)) {
                    <mat-checkbox [(ngModel)]="fields[f.key]" (change)="onFieldToggled()" [attr.data-testid]="'field-' + f.key">{{ f.label }}</mat-checkbox>
                  }
                }
              </div>
            </div>
            }
          }
          @if (isMultiGame) {
            <p class="grid-note" data-testid="report-shell-multi-field-note">
              Host payout rules are one game's arrangement with its host, so they are reported a game at a time.
            </p>
          }
        </div>

        <button mat-raised-button color="primary" class="run-btn"
                (click)="runReport()"
                [disabled]="uiState === uiStateForTemplate.ShowLoading"
                data-testid="report-shell-run">
          Run report
        </button>
      }
    </aside>

    <!-- RIGHT: results pane -->
    <section class="results-pane">

      @if (uiState === uiStateForTemplate.ShowLoading) {
        <div class="loading flex-col align-center"><mat-spinner diameter="42"></mat-spinner><span>Generating report…</span></div>
      } @else if (multiResult) {
        <section class="results" data-testid="report-shell-multi-results">
          <div class="res-head flex-row-wrap align-center justify-space-between">
            <div class="res-title">
              {{ perGameRows.length }} games
              <span class="period" data-testid="report-shell-multi-period">{{ multiResult.StartDate | date:'mediumDate':'UTC' }} – {{ multiResult.EndDate | date:'mediumDate':'UTC' }}</span>
            </div>
            <div class="exports flex-row-wrap gap-small">
              <button mat-stroked-button (click)="downloadCsv()" data-testid="report-shell-csv">↓ CSV</button>
              <button mat-stroked-button (click)="downloadPdf()" data-testid="report-shell-pdf">↓ PDF</button>
              <button mat-stroked-button (click)="print()" data-testid="report-shell-print">⎙ Print</button>
            </div>
          </div>

          @if (combinedTiles.length) {
            <div class="tiles">
              @for (t of combinedTiles; track t.label) {
                <div class="tile" data-testid="report-shell-combined-tile">
                  <div class="t-lbl">{{ t.label }}</div>
                  <div class="t-val">{{ t.currency ? (t.value | formatCurrency) : (t.value | number) }}</div>
                </div>
              }
            </div>
          }

          @if (perGameColumns.length) {
            <h4 class="grid-title">
              By game
              <span class="grid-sub">{{ perGameRows.length | number }} games · totals reconcile to the figures above</span>
            </h4>
            <div class="gridwrap">
              <table class="grid" data-testid="report-shell-per-game-grid">
                <thead>
                  <tr>
                    <th>Game</th>
                    @for (col of perGameColumns; track col.label) {
                      <th class="num">{{ col.label }}</th>
                    }
                  </tr>
                </thead>
                <tbody>
                  @for (row of perGameRows; track row.GameId) {
                    <tr data-testid="report-shell-per-game-row">
                      <td>{{ row.GameName }}</td>
                      @for (col of perGameColumns; track col.label) {
                        <td class="num">{{ cell(col, row.Report) }}</td>
                      }
                    </tr>
                  }
                  <tr class="totals-row" data-testid="report-shell-per-game-total">
                    <td>Total ({{ perGameRows.length | number }} games)</td>
                    @for (col of perGameColumns; track col.label) {
                      <td class="num">{{ cell(col, combined!) }}</td>
                    }
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- The one figure in the row above that is not the column added up, so it is said rather than assumed. -->
            @if (combined?.ActivePlayers !== undefined && combined?.ActivePlayers !== null) {
              <p class="grid-note" data-testid="report-shell-active-players-note">
                Active players is counted once across every selected game, so it is smaller than the column added up
                wherever someone played in more than one.
              </p>
            }
          }

          @if (combinedCharityRows.length) {
            <div class="grid-title-row flex align-center">
              <h4 class="grid-title">
                Charity breakdown
                @if (charityGameFilter) {
                  <span class="grid-sub" data-testid="report-shell-multi-charity-sub">{{ visibleCharityRows.length | number }} charities in {{ charityFilterGame?.GameName }} · {{ visibleCharityNet | formatCurrency }} net</span>
                } @else {
                  <span class="grid-sub" data-testid="report-shell-multi-charity-sub">{{ combined?.TotalCharities | number }} charities · {{ (combined?.TotalNetCharityContribution ?? 0) | formatCurrency }} net</span>
                }
              </h4>
              <mat-form-field appearance="outline" class="grid-filter" subscriptSizing="dynamic">
                <mat-label>Game</mat-label>
                <mat-select [(ngModel)]="charityGameFilter" data-testid="report-shell-charity-game-filter">
                  <mat-option value="" data-testid="report-shell-charity-game-filter-all">All selected games</mat-option>
                  @for (g of perGameRows; track g.GameId) {
                    <mat-option [value]="g.GameId" data-testid="report-shell-charity-game-filter-option">{{ g.GameName }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
            <div class="gridwrap">
              <table class="grid" data-testid="report-shell-multi-charity-grid">
                <thead>
                  <tr>
                    <th class="sortable" (click)="sortCharitiesBy('CharityName')" data-testid="report-shell-charity-sort-charity">Charity{{ charitySortIndicator('CharityName') }}</th>
                    <th class="sortable" (click)="sortCharitiesBy('Game')" data-testid="report-shell-charity-sort-game">Game{{ charitySortIndicator('Game') }}</th>
                    <th class="num sortable" (click)="sortCharitiesBy('GrossContribution')" data-testid="report-shell-charity-sort-gross">Gross{{ charitySortIndicator('GrossContribution') }}</th>
                    <th class="num sortable" (click)="sortCharitiesBy('RefundAmount')" [title]="refundFeesTooltip">{{ refundFeesLabel }}{{ charitySortIndicator('RefundAmount') }}</th>
                    <th class="num sortable" (click)="sortCharitiesBy('DisputeAmount')">Disputes{{ charitySortIndicator('DisputeAmount') }}</th>
                    <th class="num sortable" (click)="sortCharitiesBy('NetContribution')" data-testid="report-shell-charity-sort-net">Net{{ charitySortIndicator('NetContribution') }}</th>
                    <th class="num sortable" (click)="sortCharitiesBy('NumberOfSupporters')">Supporters{{ charitySortIndicator('NumberOfSupporters') }}</th>
                    <th class="num sortable" (click)="sortCharitiesBy('GrossSales')" [title]="charitySalesTooltip" data-testid="report-shell-charity-sort-sales">Gross sales{{ charitySortIndicator('GrossSales') }}</th>
                    <th class="num sortable" (click)="sortCharitiesBy('CausableFees')" [title]="charitySalesTooltip">Causable fees{{ charitySortIndicator('CausableFees') }}</th>
                  </tr>
                </thead>
                <tbody>
                  @for (c of visibleCharityRows; track c.CharityName) {
                    <tr data-testid="report-shell-multi-charity-row">
                      <td data-testid="report-shell-multi-charity-name">{{ c.CharityName }}</td>
                      <td data-testid="report-shell-multi-charity-game">{{ c.GameNames.join(', ') }}</td>
                      <td class="num">{{ c.GrossContribution | formatCurrency }}</td>
                      <td class="num">{{ c.RefundAmount | formatCurrency }}</td>
                      <td class="num">{{ c.DisputeAmount | formatCurrency }}</td>
                      <td class="num">{{ c.NetContribution | formatCurrency }}</td>
                      <td class="num">{{ c.NumberOfSupporters === undefined || c.NumberOfSupporters === null ? '—' : (c.NumberOfSupporters | number) }}</td>
                      <td class="num" data-testid="report-shell-multi-charity-sales">{{ c.GrossSales | formatCurrency }}</td>
                      <td class="num" data-testid="report-shell-multi-charity-causable-fees">{{ c.CausableFees | formatCurrency }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            <p class="grid-note" data-testid="report-shell-refund-fees-note">{{ refundFeesDefinition }}</p>
            @if (anyCharitySpansGames) {
              <p class="grid-note" data-testid="report-shell-supporters-note">
                Supporters are distinct players within a game, so a charity supported in more than one shows no
                combined count rather than a total that counts the same person twice.
              </p>
            }
          }

          @if (combinedBalanceRows.length) {
            <h4 class="grid-title">
              Charity / group balances
              <span class="grid-sub">{{ combinedBalanceRows.length | number }} groups · {{ (combined?.TotalOwedToCharities ?? 0) | formatCurrency }} owed</span>
            </h4>
            <div class="gridwrap">
              <table class="grid" data-testid="report-shell-multi-balances-grid">
                <thead>
                  <tr>
                    <th>Charity / group</th>
                    <th>Game</th>
                    <th>Paid through</th>
                    <th class="num">Earned</th>
                    <th class="num">Paid out</th>
                    <th class="num">Adjustments</th>
                    <th class="num">Owed</th>
                  </tr>
                </thead>
                <tbody>
                  @for (b of combinedBalanceRows; track b.CharityName) {
                    <tr data-testid="report-shell-multi-balances-row">
                      <td>{{ b.CharityName }}</td>
                      <td>{{ b.GameNames.join(', ') }}</td>
                      <td>{{ b.PaidThroughVaries ? '—' : (b.PaidThroughPayeeName || 'Paid directly') }}</td>
                      <td class="num">{{ b.TotalContributions | formatCurrency }}</td>
                      <td class="num">{{ b.TotalPayouts | formatCurrency }}</td>
                      <td class="num">{{ b.TotalAdjustments | formatCurrency }}</td>
                      <td class="num" [class.owed]="b.AmountOwed > 0">{{ b.AmountOwed | formatCurrency }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            @if (anyBalancePayeeVaries) {
              <p class="grid-note" data-testid="report-shell-payee-note">
                A charity remitted through different payees in different games shows none, rather than one of them.
              </p>
            }
          }

          @if (hasLicenceFigures) {
            <p class="grid-note" data-testid="report-shell-licence-note">
              The prize pot, organization share and total expenses cover finished draws only, as the lottery licence
              report does. {{ combinedJackpotLabel }} is the pot carried in plus what sales added; the adjustment is zero when every dollar
              of it was either awarded or carried forward. Total expenses include the pot contributions.
            </p>
          }

          @if (prizeWinners.length) {
            <h4 class="grid-title">
              Prize winners
              <span class="grid-sub">{{ prizeWinners.length | number }} winners</span>
            </h4>
            <div class="gridwrap">
              <table class="grid" data-testid="report-shell-winners-grid">
                <thead>
                  <tr>
                    @if (multiResult) {
                      <th>Game</th>
                    }
                    <th>Draw date</th>
                    <th>Winner</th>
                    <th>Phone</th>
                    <th class="num">Prize</th>
                  </tr>
                </thead>
                <tbody>
                  @for (w of prizeWinners; track $index) {
                    <tr data-testid="report-shell-winner-row">
                      @if (multiResult) {
                        <td>{{ w.GameName }}</td>
                      }
                      <td>{{ w.DrawDate | date:'mediumDate' }}</td>
                      <td>{{ winnerName(w) }}</td>
                      <td>{{ w.Phone }}</td>
                      <td class="num">{{ w.PrizeAmount | formatCurrency }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }

          @if (!combinedTiles.length && !combinedCharityRows.length && !combinedBalanceRows.length && !prizeWinners.length) {
            <p class="empty">No data for the selected fields and period.</p>
          }
        </section>
      } @else if (result) {
        <section class="results" data-testid="report-shell-results">
          <div class="res-head flex-row-wrap align-center justify-space-between">
            <div class="res-title">
              {{ selectedGame?.Name }}
              <span class="period" data-testid="report-shell-period">{{ result.StartDate | date:'mediumDate':'UTC' }} – {{ result.EndDate | date:'mediumDate':'UTC' }}</span>
            </div>
            <div class="exports flex-row-wrap gap-small">
              <button mat-stroked-button (click)="downloadCsv()" data-testid="report-shell-csv">↓ CSV</button>
              <button mat-stroked-button (click)="downloadPdf()" data-testid="report-shell-pdf">↓ PDF</button>
              <button mat-stroked-button (click)="print()" data-testid="report-shell-print">⎙ Print</button>
            </div>
          </div>

          @if (hasBreakdown) {
            <div class="seg view-toggle" data-testid="report-shell-view-toggle">
              <button type="button" [class.on]="viewMode === 'totals'" (click)="setViewMode('totals')"
                      data-testid="report-shell-view-totals">Totals</button>
              <button type="button" [class.on]="viewMode === 'byDraw'" (click)="setViewMode('byDraw')"
                      data-testid="report-shell-view-bydraw">By draw</button>
            </div>
          }

          @if (viewMode === 'byDraw' && hasBreakdown) {
            <h4 class="grid-title">
              By draw
              <span class="grid-sub">{{ breakdownRows.length | number }} draws · totals reconcile to the aggregate</span>
            </h4>
            <div class="gridwrap">
              <table class="grid" data-testid="report-shell-breakdown-grid">
                <thead>
                  <tr>
                    <th>Draw</th>
                    @for (col of breakdownColumns; track col.label) {
                      <th class="num">{{ col.label }}</th>
                    }
                  </tr>
                </thead>
                <tbody>
                  @for (row of breakdownRows; track row.GameInstanceId) {
                    <tr data-testid="report-shell-breakdown-row">
                      <td>{{ (row.DrawDate || row.EndDate) | date:'mediumDate' }}</td>
                      @for (col of breakdownColumns; track col.label) {
                        <td class="num">{{ cell(col, row) }}</td>
                      }
                    </tr>
                  }
                  <tr class="totals-row" data-testid="report-shell-breakdown-total">
                    <td>Total ({{ breakdownRows.length | number }} draws)</td>
                    @for (col of breakdownColumns; track col.label) {
                      <td class="num">{{ totalCell(col, result) }}</td>
                    }
                  </tr>
                </tbody>
              </table>
            </div>
          } @else {

          @if (statTiles.length) {
            <div class="tiles">
              @for (t of statTiles; track t.label) {
                <div class="tile">
                  <div class="t-lbl">{{ t.label }}</div>
                  <div class="t-val">{{ t.currency ? (t.value | formatCurrency) : (t.value | number) }}</div>
                </div>
              }
            </div>
          }

          @if (chart; as c) {
            <div class="trend" data-testid="report-shell-trend">
              <div class="trend-h">Gross sales per draw</div>
              <svg viewBox="0 0 600 110" preserveAspectRatio="none" class="trend-svg">
                <path [attr.d]="c.area" class="trend-area"></path>
                <path [attr.d]="c.line" class="trend-line"></path>
                <circle [attr.cx]="c.lastX" [attr.cy]="c.lastY" r="3.5" class="trend-dot"></circle>
              </svg>
            </div>
          }

          @if (result.CharityBreakdown?.length) {
            <h4 class="grid-title">
              Charity breakdown
              <span class="grid-sub">{{ result.TotalCharities | number }} charities · {{ (result.TotalNetCharityContribution ?? 0) | formatCurrency }} net</span>
            </h4>
            <div class="gridwrap">
              <table class="grid" data-testid="report-shell-charity-grid">
                <thead>
                  <tr>
                    <th>Charity</th>
                    <th class="num">Gross</th>
                    <th class="num" [title]="refundFeesTooltip">{{ refundFeesLabel }}</th>
                    <th class="num">Disputes</th>
                    <th class="num">Net</th>
                    <th class="num">Supporters</th>
                    <th class="num" [title]="charitySalesTooltip">Gross sales</th>
                    <th class="num" [title]="charitySalesTooltip">Causable fees</th>
                  </tr>
                </thead>
                <tbody>
                  @for (c of result.CharityBreakdown; track c.CharityName) {
                    <tr>
                      <td>{{ c.CharityName }}</td>
                      <td class="num">{{ c.GrossContribution | formatCurrency }}</td>
                      <td class="num">{{ c.RefundAmount | formatCurrency }}</td>
                      <td class="num">{{ c.DisputeAmount | formatCurrency }}</td>
                      <td class="num">{{ c.NetContribution | formatCurrency }}</td>
                      <td class="num">{{ c.NumberOfSupporters | number }}</td>
                      <td class="num" data-testid="report-shell-charity-sales">{{ (c.GrossSales ?? 0) | formatCurrency }}</td>
                      <td class="num" data-testid="report-shell-charity-causable-fees">{{ (c.CausableFees ?? 0) | formatCurrency }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            <p class="grid-note" data-testid="report-shell-single-refund-fees-note">{{ refundFeesDefinition }}</p>
          }

          @if (hasLicenceFigures) {
            <p class="grid-note" data-testid="report-shell-licence-note">
              The prize pot, organization share and total expenses cover finished draws only, as the lottery licence
              report does. {{ singleJackpotLabel }} is the pot carried in plus what sales added; the adjustment is zero when every dollar
              of it was either awarded or carried forward. Total expenses include the pot contributions.
            </p>
          }

          @if (prizeWinners.length) {
            <h4 class="grid-title">
              Prize winners
              <span class="grid-sub">{{ prizeWinners.length | number }} winners</span>
            </h4>
            <div class="gridwrap">
              <table class="grid" data-testid="report-shell-winners-grid">
                <thead>
                  <tr>
                    @if (multiResult) {
                      <th>Game</th>
                    }
                    <th>Draw date</th>
                    <th>Winner</th>
                    <th>Phone</th>
                    <th class="num">Prize</th>
                  </tr>
                </thead>
                <tbody>
                  @for (w of prizeWinners; track $index) {
                    <tr data-testid="report-shell-winner-row">
                      @if (multiResult) {
                        <td>{{ w.GameName }}</td>
                      }
                      <td>{{ w.DrawDate | date:'mediumDate' }}</td>
                      <td>{{ winnerName(w) }}</td>
                      <td>{{ w.Phone }}</td>
                      <td class="num">{{ w.PrizeAmount | formatCurrency }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }

          @if (hasBalances) {
            <h4 class="grid-title">
              Charity / group balances
              <span class="grid-sub">{{ balanceRows.length | number }} groups · {{ (result.TotalOwedToCharities ?? 0) | formatCurrency }} owed</span>
            </h4>
            <div class="gridwrap">
              <table class="grid" data-testid="report-shell-balances-grid">
                <thead>
                  <tr>
                    <th>Charity / group</th>
                    <!-- Who the money is actually remitted to. "Paid directly" rather than a blank, so the
                         column answers the question on every row instead of only the unusual ones (#1411). -->
                    <th>Paid through</th>
                    <th class="num">Earned</th>
                    <th class="num">Paid out</th>
                    <!-- Owed is earned + adjustments - paid out, so the adjustments column is what makes
                         the row's arithmetic close on screen. -->
                    <th class="num">Adjustments</th>
                    <th class="num">Owed</th>
                  </tr>
                </thead>
                <tbody>
                  @for (b of balanceRows; track b.CharityId) {
                    <tr data-testid="report-shell-balances-row">
                      <td>{{ b.CharityName }}</td>
                      <td>{{ b.PaidThroughPayeeName || 'Paid directly' }}</td>
                      <td class="num">{{ b.TotalContributions | formatCurrency }}</td>
                      <td class="num">{{ b.TotalPayouts | formatCurrency }}</td>
                      <td class="num">{{ b.TotalAdjustments | formatCurrency }}</td>
                      <td class="num" [class.owed]="b.AmountOwed > 0">{{ b.AmountOwed | formatCurrency }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }

          @if (result.HostPayoutRuleVoidedVersionInPeriod) {
            <p class="grid-note" data-testid="report-shell-payout-rule-voided-note">
              A payout rule version that covered part of this period has since been voided, so the host admin fee and
              payable here may differ from a copy of this report run before the void.
            </p>
          }

          @if (hasHostAdminFee) {
            <h4 class="grid-title" data-testid="report-shell-host-fee-heading">
              Host admin fee
              <span class="grid-sub">{{ (result.HostAdminFeeTotal ?? 0) | formatCurrency }} split {{ hostFeeRows.length }} way(s)</span>
            </h4>

            <!-- The fee is split once on the aggregate, so a window that straddles a rule change is split by one
                 version of the rule. Saying which, and saying that it changed, is the difference between a figure
                 a reader can act on and one they will misread as covering the whole period on these terms. -->
            @if (result.HostPayoutRuleChangedDuringPeriod) {
              <p class="grid-note" data-testid="report-shell-payout-rule-changed-note">
                The payout rules changed during this period. These figures are split by the rules in effect from
                {{ formatRuleEffectiveFrom(result.HostPayoutRuleEffectiveFrom) }}, applied to the whole period.
              </p>
            } @else if (result.HostPayoutRuleEffectiveFrom) {
              <p class="grid-note" data-testid="report-shell-payout-rule-note">
                Split by the payout rules in effect from {{ formatRuleEffectiveFrom(result.HostPayoutRuleEffectiveFrom) }}.
              </p>
            }
            <div class="gridwrap">
              <table class="grid" data-testid="report-shell-host-fee-grid">
                <thead>
                  <tr>
                    <th>Recipient</th>
                    <th class="num">Share</th>
                    <th class="num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  @for (a of hostFeeRows; track a.RecipientName) {
                    <tr data-testid="report-shell-host-fee-row">
                      <td>{{ a.RecipientName }}</td>
                      <td class="num">{{ a.SharePercent }}%</td>
                      <td class="num">{{ a.Amount | formatCurrency }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            <!-- Asked twice in #1411: why the charity figures do not move when the fee is switched on. They
                 are already net of it, and a note here is where the question actually gets asked. -->
            <p class="grid-note" data-testid="report-shell-host-fee-note">
              Already deducted from each payment before the group figures above — the shares name who
              receives this fee, they do not reduce the groups a second time.
            </p>
          }

          @if (hasHostPayable) {
            <h4 class="grid-title">
              Payable to host organization
              <span class="grid-sub" data-testid="report-shell-host-payable">
                {{ result.HostPayableRecipientName }} · {{ (result.HostPayableAmount ?? 0) | formatCurrency }}
              </span>
            </h4>
            <div class="gridwrap">
              <table class="grid" data-testid="report-shell-host-payable-grid">
                <tbody>
                  @for (line of hostPayableLines; track line.ComponentType) {
                    <tr data-testid="report-shell-host-payable-row">
                      <td>{{ line.IsAddition ? '+' : '−' }} {{ line.Label }}</td>
                      <td class="num">{{ line.SignedAmount | formatCurrency }}</td>
                    </tr>
                  }
                  <!-- The total sits under its own parts so the arithmetic is checkable on the page. -->
                  <tr class="totals-row" data-testid="report-shell-host-payable-total">
                    <td>Total payable</td>
                    <td class="num">{{ (result.HostPayableAmount ?? 0) | formatCurrency }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          }

          @if (!statTiles.length && !result.CharityBreakdown?.length && !hasBalances && !hasHostAdminFee && !hasHostPayable && !prizeWinners.length) {
            <p class="empty">No data for the selected fields and period.</p>
          }

          }
        </section>
      } @else {
        <div class="results-placeholder flex-col align-center justify-center text-center" data-testid="report-shell-placeholder">
          <div class="ph-icon">📊</div>
          <p>{{ hasScope ? 'Choose your fields and timeframe, then run the report.' : 'Choose a game to begin.' }}</p>
        </div>
      }

    </section>

  </div>
</c-container>
